import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import api from "../../config/axios";
import NaturezaServicoSelect from "./NaturezaServicoSelect";
import { DocumentArrowUpIcon } from "@heroicons/react/24/outline";

const TaxGenerationForm = () => {
  const [formData, setFormData] = useState({
    fornecedorNome: "",
    fornecedorCnpj: "",
    dataEmissao: "",
    numeroNota: "",
    serieNota: "0", // Valor padrão Série 0
    regime: "SIMPLES", // SIMPLES, PRESUMIDO, REAL
    naturezaServico: "",
    valorNota: "",
    aliqRetencao: "",
    valorRetencaoIRRF: "",
    valorRetencaoISS: "",
    liquidoApagar: "",
    arquivoImagem: null, // Caminho da imagem (convertida no caso de PDF)
    arquivoOriginal: null, // Caminho do arquivo original (PDF ou imagem)
  });

  const [loading, setLoading] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [payerSources, setPayerSources] = useState([]);
  const [selectedPayerSource, setSelectedPayerSource] = useState("");
  const [uploadStatus, setUploadStatus] = useState({
    loading: false,
    error: null,
  });

  useEffect(() => {
    const fetchPayerSources = async () => {
      try {
        const response = await api.get('/payer-sources');
        setPayerSources(response.data);
      } catch (error) {
        console.error("Erro ao buscar fontes pagadoras:", error);
        // Não mostrar erro ao usuário se for apenas lista vazia ou erro de rede silencioso, 
        // mas aqui vamos logar. Se falhar, o select fica vazio.
      }
    };
    fetchPayerSources();
  }, []);

  const handlePayerSourceChange = (e) => {
    const sourceId = e.target.value;
    setSelectedPayerSource(sourceId);
  };

  const regimeOptions = [
    { value: "SIMPLES", label: "Simples Nacional" },
    { value: "PRESUMIDO", label: "Lucro Presumido" },
    { value: "REAL", label: "Lucro Real" },
  ];

  const serieOptions = [
    { value: "0", label: "Série 0" },
    { value: "1", label: "Série 1" },
    { value: "2", label: "Série 2" },
  ];

  const formatCurrency = (value) => {
    if (!value && value !== 0) return "";

    // Se for string, converte para número
    let number =
      typeof value === "string"
        ? parseFloat(value.replace(/\./g, "").replace(",", "."))
        : value;

    // Formata o número para o padrão brasileiro
    return number.toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const parseCurrency = (value) => {
    if (!value && value !== 0) return 0;
    // Remove pontos do milhar e converte vírgula em ponto
    return parseFloat(value.toString().replace(/\./g, "").replace(",", "."));
  };

  const calculateLiquidoApagar = (valorNotaStr, valorRetencaoIRRFStr, valorRetencaoISSStr) => {
    const valorNotaNum = parseCurrency(valorNotaStr);
    const valorRetencaoIRRFNum = parseCurrency(valorRetencaoIRRFStr);
    const valorRetencaoISSNum = parseCurrency(valorRetencaoISSStr);
    return valorNotaNum - valorRetencaoIRRFNum - valorRetencaoISSNum;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "valorNota") {
      // Remove tudo exceto números e vírgula
      const cleanValue = value.replace(/[^\d,]/g, "");

      setFormData((prev) => {
        const newFormData = {
          ...prev,
          valorNota: cleanValue,
        };

        // Se tiver valor da nota e alíquota, recalcula
        if (cleanValue && prev.aliqRetencao) {
          const valorNotaNum = parseCurrency(cleanValue);
          const aliquotaNum = parseCurrency(prev.aliqRetencao);

          // Calcula valor de retenção (valor * aliquota / 100)
          const valorRetencaoIRRF = (valorNotaNum * aliquotaNum) / 100;
          const liquidoAPagar = calculateLiquidoApagar(
            cleanValue,
            formatCurrency(valorRetencaoIRRF),
            prev.valorRetencaoISS || "0,00"
          );

          newFormData.valorRetencaoIRRF = formatCurrency(valorRetencaoIRRF);
          newFormData.liquidoApagar = formatCurrency(liquidoAPagar);
        } else if (cleanValue) {
          const liquidoAPagar = calculateLiquidoApagar(
            cleanValue,
            prev.valorRetencaoIRRF || "0,00",
            prev.valorRetencaoISS || "0,00"
          );
          newFormData.liquidoApagar = formatCurrency(liquidoAPagar);
        }

        return newFormData;
      });
    } else if (name === "valorRetencaoISS") {
      const cleanValue = value.replace(/[^\d,]/g, "");
      setFormData((prev) => {
        const liquidoAPagar = calculateLiquidoApagar(
          prev.valorNota || "0,00",
          prev.valorRetencaoIRRF || "0,00",
          cleanValue || "0,00"
        );

        return {
          ...prev,
          valorRetencaoISS: cleanValue,
          liquidoApagar: formatCurrency(liquidoAPagar),
        };
      });
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleNaturezaChange = async (naturezaId) => {
    try {
      if (naturezaId) {
        const response = await api.get(`/naturezas-rendimento/${naturezaId}`);
        const deducoes = response.data?.deducoes;

        setFormData((prev) => {
          const newFormData = {
            ...prev,
            naturezaServico: naturezaId,
            aliqRetencao: deducoes
              ? deducoes.toString().replace(".", ",")
              : "0,00",
          };

          // Se já tiver um valor da nota, recalcula os valores
          if (prev.valorNota && deducoes) {
            const valorNotaNum = parseCurrency(prev.valorNota);
            const aliquotaNum = deducoes;

            // Calcula valor de retenção (valor * aliquota / 100)
            const valorRetencaoIRRF = (valorNotaNum * aliquotaNum) / 100;
            const liquidoAPagar = calculateLiquidoApagar(
              prev.valorNota,
              formatCurrency(valorRetencaoIRRF),
              prev.valorRetencaoISS || "0,00"
            );

            newFormData.valorRetencaoIRRF = formatCurrency(valorRetencaoIRRF);
            newFormData.liquidoApagar = formatCurrency(liquidoAPagar);
          }

          return newFormData;
        });
      } else {
        setFormData((prev) => ({
          ...prev,
          naturezaServico: "",
          aliqRetencao: "",
          valorRetencaoIRRF: "0,00",
          liquidoApagar: formatCurrency(
            calculateLiquidoApagar(prev.valorNota || "0,00", "0,00", prev.valorRetencaoISS || "0,00")
          ),
        }));
      }
    } catch (error) {
      toast.error("Erro ao buscar alíquota da natureza do serviço");
      setFormData((prev) => ({
        ...prev,
        aliqRetencao: "0,00",
        valorRetencaoIRRF: "0,00",
        liquidoApagar: formatCurrency(
          calculateLiquidoApagar(prev.valorNota || "0,00", "0,00", prev.valorRetencaoISS || "0,00")
        ),
      }));
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validação do tipo de arquivo
    const validTypes = [
      "image/jpeg",
      "image/png",
      "image/gif",
      "application/pdf",
    ];
    if (!validTypes.includes(file.type)) {
      toast.error(
        "Tipo de arquivo não suportado. Use PDF ou imagem (JPEG, PNG, GIF)."
      );
      return;
    }

    setUploadStatus({ loading: true, error: null });

    try {
      // Se for PDF, converte para imagem primeiro
      if (file.type === "application/pdf") {
        try {
          toast.info("Processando PDF...", {
            autoClose: false,
            toastId: "pdf-conversion",
          });

          const formData = new FormData();
          formData.append("file", file);

          const response = await api.post(
            "/notas-fiscais/convert-pdf",
            formData,
            {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            }
          );

          toast.dismiss("pdf-conversion");

          if (response.data.success) {
            const imagePath = response.data.imagePath;

            setFormData((prev) => ({
              ...prev,
              arquivoOriginal: file,
              arquivoImagem: imagePath,
            }));

            setPreviewImage("success");
            toast.success("Nota carregada com sucesso!");
          } else {
            throw new Error("Falha ao processar PDF");
          }
        } catch (error) {
          toast.error("Erro ao processar o PDF. Tente novamente.");
          throw error;
        }
      } else {
        // Se for imagem, usa o mesmo arquivo para ambos
        setFormData((prev) => ({
          ...prev,
          arquivoOriginal: file,
          arquivoImagem: file,
        }));

        setPreviewImage("success");
      }
      setUploadStatus({ loading: false, error: null });
    } catch (error) {
      toast.dismiss("pdf-conversion");
      setUploadStatus({
        loading: false,
        error:
          error.response?.data?.message ||
          error.message ||
          "Erro ao processar arquivo",
      });
      toast.error(
        error.response?.data?.message ||
          "Erro ao processar arquivo. Tente novamente."
      );
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formDataToSend = new FormData();

      // Campos de texto normais
      const textFields = [
        "fornecedorNome",
        "fornecedorCnpj",
        "numeroNota",
        "serieNota",
        "regime",
        "naturezaServico",
      ];
      textFields.forEach((field) => {
        if (formData[field]) {
          formDataToSend.append(field, formData[field]);
        }
      });

      // Payer Source ID
      if (selectedPayerSource) {
          formDataToSend.append("payerSourceId", selectedPayerSource);
      }

      // Data de emissão
      if (formData.dataEmissao) {
        formDataToSend.append("dataEmissao", formData.dataEmissao);
      }

      // Campos numéricos (convertendo string para número)
      const numericFields = [
        "valorNota",
        "aliqRetencao",
        "valorRetencaoIRRF",
        "valorRetencaoISS",
        "liquidoApagar",
      ];
      numericFields.forEach((field) => {
        if (formData[field]) {
          const value = formData[field].replace(/\./g, "").replace(",", ".");
          formDataToSend.append(field, parseFloat(value));
        }
      });

      // Adiciona os arquivos
      if (formData.arquivoOriginal) {
        // Se for PDF, envia tanto o PDF original quanto a imagem convertida
        if (formData.arquivoOriginal.type === "application/pdf") {
          formDataToSend.append("file", formData.arquivoOriginal);
          formDataToSend.append("arquivoImagem", formData.arquivoImagem);
        } else {
          // Se for imagem, envia apenas o arquivo original
          formDataToSend.append("file", formData.arquivoOriginal);
        }
      }

      const response = await api.post("/notas-fiscais", formDataToSend, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.status === 201) {
        toast.success("Nota fiscal salva com sucesso!", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });

        // Limpa o formulário
        setSelectedPayerSource("");
        setFormData({
          fornecedorNome: "",
          dataEmissao: "",
          numeroNota: "",
          serieNota: "0",
          regime: "SIMPLES",
          naturezaServico: "",
          valorNota: "",
          aliqRetencao: "",
          valorRetencaoIRRF: "",
          valorRetencaoISS: "",
          liquidoApagar: "",
          arquivoImagem: null,
          arquivoOriginal: null,
        });
        setPreviewImage(null);
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        "Erro ao salvar nota fiscal. Tente novamente.";
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto bg-slate-900 rounded-xl shadow-lg border border-slate-800">
      <div className="p-8">
        <h2 className="text-2xl font-semibold text-white mb-8">
          Gerar Imposto
        </h2>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Seção: Fonte Pagadora */}
          <div className="bg-slate-800 p-6 rounded-lg shadow-sm border border-slate-700">
            <h3 className="text-lg font-medium text-white mb-6 flex items-center gap-2">
              <span className="w-1 h-6 bg-indigo-500 rounded-full"></span>
              Fonte Pagadora
            </h3>
            <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                    Selecione a Fonte Pagadora
                </label>
                <select
                    value={selectedPayerSource}
                    onChange={handlePayerSourceChange}
                    className="w-full px-4 py-2.5 rounded-lg border border-slate-600 focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-slate-900 text-white"
                    required
                >
                    <option value="">Selecione...</option>
                    {payerSources.map(source => (
                        <option key={source.id} value={source.id}>{source.nomeFonte}</option>
                    ))}
                </select>
            </div>
          </div>

          {/* Seção: Informações Básicas */}
          <div className="bg-slate-800 p-6 rounded-lg shadow-sm border border-slate-700">
            <h3 className="text-lg font-medium text-white mb-6 flex items-center gap-2">
              <span className="w-1 h-6 bg-indigo-500 rounded-full"></span>
              Informações da Nota Fiscal
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-6">
              {/* Nome do Fornecedor */}
              <div className="col-span-1 md:col-span-2 lg:col-span-2">
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Nome do Fornecedor
                </label>
                <input
                  type="text"
                  name="fornecedorNome"
                  value={formData.fornecedorNome}
                  onChange={handleChange}
                  className="w-full px-4 py-2.5 rounded-lg border border-slate-600 bg-slate-900 text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent placeholder-slate-500"
                  required
                />
              </div>

              {/* CNPJ do Fornecedor */}
              <div className="col-span-1">
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  CNPJ do Fornecedor
                </label>
                <input
                  type="text"
                  name="fornecedorCnpj"
                  value={formData.fornecedorCnpj}
                  onChange={handleChange}
                  className="w-full px-4 py-2.5 rounded-lg border border-slate-600 bg-slate-900 text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent placeholder-slate-500"
                  placeholder="00.000.000/0000-00"
                />
              </div>

              {/* Data de Emissão */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Data de Emissão
                </label>
                <input
                  type="date"
                  name="dataEmissao"
                  value={formData.dataEmissao}
                  onChange={handleChange}
                  className="w-full px-4 py-2.5 rounded-lg border border-slate-600 bg-slate-900 text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent [color-scheme:dark]"
                  required
                />
              </div>

              {/* Número da Nota */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Número da Nota
                </label>
                <input
                  type="text"
                  name="numeroNota"
                  value={formData.numeroNota}
                  onChange={handleChange}
                  className="w-full px-4 py-2.5 rounded-lg border border-slate-600 bg-slate-900 text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent placeholder-slate-500"
                  required
                />
              </div>

              {/* Série da Nota */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Série da Nota
                </label>
                <select
                  name="serieNota"
                  value={formData.serieNota}
                  onChange={handleChange}
                  className="w-full px-4 py-2.5 rounded-lg border border-slate-600 bg-slate-900 text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  required
                >
                  {serieOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Regime */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Regime
                </label>
                <select
                  name="regime"
                  value={formData.regime}
                  onChange={handleChange}
                  className="w-full px-4 py-2.5 rounded-lg border border-slate-600 bg-slate-900 text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  required
                >
                  {regimeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Natureza do Serviço */}
              <div className="col-span-1 md:col-span-2 lg:col-span-2">
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Natureza do Serviço
                </label>
                <NaturezaServicoSelect
                  value={formData.naturezaServico}
                  onChange={handleNaturezaChange}
                  darkMode={true}
                />
              </div>
            </div>
          </div>

          {/* Seção de Valores */}
          <div className="bg-slate-800 p-6 rounded-lg shadow-sm border border-slate-700">
            <h3 className="text-lg font-medium text-white mb-6 flex items-center gap-2">
              <span className="w-1 h-6 bg-indigo-500 rounded-full"></span>
              Valores
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Valor da Nota */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Valor da Nota
                </label>
                <input
                  type="text"
                  name="valorNota"
                  value={formData.valorNota}
                  onChange={handleChange}
                  className="w-full px-4 py-2.5 rounded-lg border border-slate-600 bg-slate-900 text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent placeholder-slate-500"
                  required
                  placeholder="R$ 0,00"
                  onBlur={(e) => {
                    const formatted = formatCurrency(e.target.value);
                    setFormData((prev) => ({
                      ...prev,
                      valorNota: formatted,
                    }));
                  }}
                />
              </div>

              {/* Alíquota de Retenção */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Alíquota de Retenção (%)
                </label>
                <input
                  type="text"
                  name="aliqRetencao"
                  value={formData.aliqRetencao}
                  readOnly
                  className="w-full px-4 py-2.5 rounded-lg border border-slate-600 bg-slate-950 focus:ring-2 focus:ring-indigo-500 focus:border-transparent font-medium text-slate-400 cursor-not-allowed"
                  placeholder="0,00"
                />
              </div>
            </div>
          </div>

          {/* Seção de Retenção e Líquido */}
          <div className="bg-slate-800 p-6 rounded-xl shadow-sm border border-slate-700">
            <div className="grid grid-cols-3 gap-4">
              {/* Valor Retenção IRRF */}
              <div className="text-center">
                <label className="block text-sm font-medium text-slate-400 mb-1">
                  Retenção IRRF
                </label>
                <div className="bg-slate-900 rounded-lg shadow-sm p-3 border border-indigo-900/50">
                  <input
                    type="text"
                    name="valorRetencaoIRRF"
                    value={formData.valorRetencaoIRRF}
                    readOnly
                    className="w-full text-center text-lg font-bold text-indigo-400 bg-transparent border-none focus:ring-0"
                    placeholder="R$ 0,00"
                  />
                </div>
              </div>

              {/* Valor Retenção ISS */}
              <div className="text-center">
                <label className="block text-sm font-medium text-slate-400 mb-1">
                  Retenção ISS
                </label>
                <div className="bg-slate-900 rounded-lg shadow-sm p-3 border border-purple-900/50">
                  <input
                    type="text"
                    name="valorRetencaoISS"
                    value={formData.valorRetencaoISS}
                    onChange={handleChange}
                    onBlur={(e) => {
                      const formatted = formatCurrency(e.target.value);
                      setFormData((prev) => {
                        const liquidoAPagar = calculateLiquidoApagar(
                          prev.valorNota || "0,00",
                          prev.valorRetencaoIRRF || "0,00",
                          formatted || "0,00"
                        );
                        return {
                          ...prev,
                          valorRetencaoISS: formatted,
                          liquidoApagar: formatCurrency(liquidoAPagar),
                        };
                      });
                    }}
                    className="w-full text-center text-lg font-bold text-purple-400 bg-transparent border-none focus:ring-0"
                    placeholder="R$ 0,00"
                  />
                </div>
              </div>

              {/* Líquido a Pagar */}
              <div className="text-center">
                <label className="block text-sm font-medium text-slate-400 mb-1">
                  Líquido a Pagar
                </label>
                <div className="bg-slate-900 rounded-lg shadow-sm p-3 border border-pink-900/50">
                  <input
                    type="text"
                    name="liquidoApagar"
                    value={formData.liquidoApagar}
                    readOnly
                    className="w-full text-center text-lg font-bold text-pink-400 bg-transparent border-none focus:ring-0"
                    placeholder="R$ 0,00"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Seção: Upload */}
          <div className="bg-slate-800 p-6 rounded-lg shadow-sm border border-slate-700">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-medium text-white flex items-center gap-2">
                <span className="w-1 h-6 bg-indigo-500 rounded-full"></span>
                Anexo
              </h3>
              {previewImage && (
                <div className="relative">
                  <div className="bg-slate-900 px-4 py-2 rounded-lg shadow-sm border border-green-500/50">
                    <span className="text-green-400 font-bold">
                      Arquivo carregado com sucesso!
                    </span>
                  </div>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      setPreviewImage(null);
                      setFormData((prev) => ({
                        ...prev,
                        arquivoImagem: null,
                        arquivoOriginal: null,
                      }));
                    }}
                    className="absolute -top-2 -right-2 bg-slate-800 text-slate-400 rounded-full w-6 h-6 flex items-center justify-center shadow-md hover:bg-slate-700 transition-colors border border-slate-600"
                    title="Remover arquivo"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                </div>
              )}
            </div>
            <div className="mt-2">
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Nota Fiscal
              </label>
              <label className="flex justify-center w-full h-32 px-4 transition bg-slate-900 border-2 border-slate-600 border-dashed rounded-md appearance-none cursor-pointer hover:border-slate-500 hover:bg-slate-800/50 focus:outline-none">
                <span className="flex items-center space-x-2">
                  <DocumentArrowUpIcon className="w-6 h-6 text-slate-400" />
                  <span className="font-medium text-slate-400">
                    Arraste um arquivo ou clique para selecionar
                  </span>
                </span>
                <input
                  type="file"
                  name="file-upload"
                  className="hidden"
                  onChange={handleFileChange}
                  accept=".pdf,.jpg,.jpeg,.png,.gif"
                />
              </label>
            </div>
          </div>

          {/* Botão Salvar */}
          <div className="flex justify-end pt-6">
            <button
              type="submit"
              disabled={loading}
              className={`px-6 py-3 text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-900 focus:ring-indigo-500 transition-colors duration-200 ${
                loading ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              {loading ? (
                <span className="flex items-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Salvando...
                </span>
              ) : (
                "Salvar"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TaxGenerationForm;
